import { useState } from 'react';
import classNames from 'classnames';
import { flexRender, type ColumnDef, type Table as TanstackTable } from '@tanstack/react-table';
import { type ColumnSettings } from '@gorilla/widgets-shared/src/schemas';
import { type Settings, type ViewerBoardItem, type ViewerBoardInfo, type ViewerAsset } from './types';
import { Value } from './Value';
import { getValue } from './Viewer';
import { getLabel } from './utils';

type AssetsById = Record<string, ViewerAsset>;

type TableProps = {
  settings: Settings;
  columns: ColumnDef<ViewerBoardItem, string>[];
  table: TanstackTable<ViewerBoardItem>;
  boardInfo: ViewerBoardInfo;
  noBoardItems: boolean;
  mode: 'preview' | 'publish';
  widgetId: string;
  assetsById: AssetsById;
  columnSettings: ColumnSettings[keyof ColumnSettings];
  previewTumbnailGeneratorUrl?: string;
};

export function Table({
  table,
  settings,
  boardInfo,
  noBoardItems,
  mode,
  widgetId,
  assetsById,
  columnSettings,
  previewTumbnailGeneratorUrl,
}: TableProps) {
  const [visibleSubitemRowIds, setVisibleSubitemRowIds] = useState<{ [key: string]: boolean }>({});
  const headerGroups = table.getHeaderGroups();
  const hasSubtable = settings.enableSubitemsSupport && boardInfo.subboard && boardInfo.subboard.fields.length > 0;
  const hasVisibleSubitems = Object.values(visibleSubitemRowIds).some((isVisible) => isVisible);

  if (headerGroups.some((headerGroup) => headerGroup.headers.length < 1)) {
    return (
      <div className="table">
        <table>
          <tbody>
            <tr>
              <td colSpan={200}>
                <div style={{ padding: '20px', textAlign: 'center' }}>
                  <i className="fa-regular fa-circle-exclamation"></i>&nbsp; All board columns are hidden.
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className={`table content-alignment-${settings.textAlignment}`}>
      <table>
        {settings.tableShowHeader ? (
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {/* Subitems */}
                {hasSubtable && table.getRowModel().rows.length > 0 ? <th></th> : null}

                {headerGroup.headers.map((header) => (
                  <th key={header.id}>
                    <div className="column_header" onClick={header.column.getToggleSortingHandler()}>
                      <span className="label">
                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                      </span>
                      <span className="icon">
                        <i
                          className={classNames('fa-solid', {
                            'fa-sort': header.column.getIsSorted() === false,
                            'fa-caret-up': header.column.getIsSorted() === 'desc',
                            'fa-caret-down': header.column.getIsSorted() === 'asc',
                          })}
                        ></i>
                      </span>
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
        ) : null}
        <tbody className={hasSubtable && hasVisibleSubitems ? 'subitems-active' : ''}>
          {table.getRowModel().rows.length === 0 ? (
            <tr>
              <td colSpan={200}>
                <div style={{ padding: '20px', textAlign: 'center' }}>
                  {getLabel(noBoardItems ? 'nothing_to_show' : 'no_results_found', settings)}
                </div>
              </td>
            </tr>
          ) : null}
          {table.getRowModel().rows.map((row, index) => {
            let subitemsContent;
            const subitemsLen = row.original?.subitems?.length || 0;

            if (subitemsLen && hasSubtable && visibleSubitemRowIds[row.id]) {
              const subitemsRowsContent = (
                <tbody>
                  {(row.original.subitems || []).map((subitem) => {
                    return (
                      <tr key={subitem.id}>
                        {boardInfo.subboard!.fields.map((field) => {
                          const value = getValue(subitem, field, settings);

                          return (
                            <td key={field.id}>
                              <div className="spanholder">
                                <Value
                                  value={value}
                                  field={field}
                                  settings={settings}
                                  mode={mode}
                                  widgetId={widgetId}
                                  assetsById={assetsById}
                                  columnSettings={columnSettings}
                                  previewTumbnailGeneratorUrl={previewTumbnailGeneratorUrl}
                                />
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              );

              subitemsContent = (
                <tr className="subitems-row">
                  <td></td>
                  <td colSpan={row.getVisibleCells().length + 1}>
                    <div className="subitems-table">
                      <table>
                        <thead>
                          <tr>
                            {boardInfo.subboard!.fields.map((field) => (
                              <th key={field.id}>
                                <div className="column_header">
                                  <span className="label">{field.label}</span>
                                </div>
                              </th>
                            ))}
                          </tr>
                        </thead>
                        {subitemsRowsContent}
                      </table>
                    </div>
                  </td>
                </tr>
              );
            }

            return (
              <>
                <tr
                  key={row.id}
                  className={hasSubtable && visibleSubitemRowIds[row.id] ? 'main--item subitems-current' : 'main--item'}
                  data-zebra={index % 2 === 0 ? 'odd' : 'even'}
                >
                  {/* Subitems */}
                  {hasSubtable ? (
                    subitemsLen ? (
                      <td
                        className={visibleSubitemRowIds[row.id] ? 'subitems-trigger subitems-trigger--active' : 'subitems-trigger'}
                        onClick={() => {
                          setVisibleSubitemRowIds((prev) => ({
                            ...prev,
                            [row.id]: !prev[row.id],
                          }));
                        }}
                      >
                        <i className="fa-regular fa-chevron-right"></i>
                      </td>
                    ) : (
                      <td className="subitems-trigger" style={{ cursor: 'default', pointerEvents: 'none' }}>
                        <div style={{ opacity: 0 }}>
                          <i className="fa-regular fa-chevron-right"></i>
                        </div>
                      </td>
                    )
                  ) : null}

                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                  ))}
                </tr>
                {subitemsContent}
              </>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
